import React, {
    createContext,
    useContext,
    useEffect,
    useMemo,
    useState,
} from "react";

import { selectSetting } from "../../../../../features/global/globalSlice";
import { useAppSelector } from "../../../../../hooks/reduxHooks";
import { AsyncStatuses, SortOrders } from "../../../../../types/shared";
import { Filters, SortColumns } from "../types";
import { IndexContextProps, IndexContextValues } from "../types/contexts";

const IndexContext = createContext<IndexContextValues | null>(null);

export const useIndexContext = () => {
    const context = useContext(IndexContext);
    if (!context) {
        throw new Error("useIndexContext must be used within a IndexProvider");
    }
    return context;
};

export const IndexProvider: React.FC<IndexContextProps> = props => {
    const { children } = props;

    const { teams: loadedTeams } = useAppSelector(state => state.clubhouse);
    const [sortColumn, setSortColumn] = useState<SortColumns>("team");
    const [sortOrder, setSortOrder] = useState<SortOrders>("ascending");
    const currentYear = parseInt(
        useAppSelector(state =>
            selectSetting(state, "majorybl_current_league_year"),
        )?.setting,
    );
    const [fetchStatus, setFetchStatus] = useState<AsyncStatuses>("init");

    const [filters, setFilters] = useState<Filters>({
        search: undefined,
        ageLevels: {
            options: [],
            filter: undefined,
        },
        leagues: {
            options: [],
            filter: undefined,
        },
        activeLeague: "Youth",
    });

    useEffect(() => {
        setFilters(prev => ({
            ...prev,
            ageLevels: {
                ...prev.ageLevels,
                options: Array.from(
                    new Set(
                        teams
                            .filter(
                                t =>
                                    t.currentAgeLevel.season.season ===
                                    filters.activeLeague,
                            )
                            .flatMap(l => l.ageLevels.map(a => a.ageLevel)),
                    ),
                ).sort((a, b) => parseInt(a) - parseInt(b)),
            },
            leagues: {
                ...prev.leagues,
                options: (
                    Array.from(
                        new Set(
                            teams
                                .filter(t => t.leagues !== undefined)
                                .filter(
                                    t =>
                                        t.currentAgeLevel.season.season ===
                                        filters.activeLeague,
                                )
                                .flatMap(t =>
                                    t.leagues
                                        ?.filter(l => l.year === currentYear)
                                        .map(l => l.name),
                                )
                                .filter(name => name !== undefined),
                        ),
                    ) as string[]
                ).sort((a, b) => {
                    const ageLevelA = parseInt(a.match(/\d+/)?.[0] || "0");
                    const ageLevelB = parseInt(b.match(/\d+/)?.[0] || "0");

                    const designationA = a.match(/\s(.*)$/)?.[0] || "";
                    const designationB = b.match(/\s(.*)$/)?.[0] || "";

                    if (ageLevelA !== ageLevelB) {
                        return ageLevelA - ageLevelB;
                    }

                    return designationA.localeCompare(designationB);
                }),
            },
        }));
    }, [filters.activeLeague]);

    const teams = loadedTeams.map(team => {
        // Gets the ageLevel of the currently assigned league
        const leagueAgeLevel = team.ageLevels.find(a => {
            return a.year === currentYear && a.season.id !== 4;
        });

        // Get the most recent registered age level for a team. This will be displayed in the event a team does not have an assigned league for the current season, such as for a new registration or when the year/season changes
        const teamAgeLevel = team.ageLevels.reduce((prev, current) => {
            return new Date(current.year, current.season.id) >
                new Date(prev.year, prev.season.id)
                ? current
                : prev;
        }, team.ageLevels[0]);

        const division = team.leagues
            ?.find(l => l.year === currentYear)
            ?.name.match(/ ([A-Z+])$/);

        return {
            ...team,
            currentAgeLevel: leagueAgeLevel || teamAgeLevel,
            currentDivision: division ? division[1] : "\xFF",
        };
    });

    const sortedTeams = useMemo(() => {
        let ageLevelComparison = 0;
        let teamComparison = 0;
        let firstNameComparison = 0;
        let lastNameComparison = 0;
        let primaryComparison = 0;

        return [...teams].sort((a, b) => {
            ageLevelComparison =
                parseInt(a.currentAgeLevel.ageLevel) -
                parseInt(b.currentAgeLevel.ageLevel);

            if (ageLevelComparison === 0) {
                ageLevelComparison = a.currentDivision.localeCompare(
                    b.currentDivision,
                );
            }

            teamComparison = a.name.localeCompare(b.name);
            firstNameComparison = a.coach.firstName.localeCompare(
                b.coach.firstName,
            );
            lastNameComparison = a.coach.lastName.localeCompare(
                b.coach.lastName,
            );

            switch (sortColumn) {
                case "age":
                    if (ageLevelComparison === 0) {
                        primaryComparison = teamComparison;
                    } else {
                        primaryComparison =
                            sortOrder === "ascending"
                                ? ageLevelComparison
                                : -ageLevelComparison;
                    }
                    break;
                case "team":
                    if (teamComparison === 0) {
                        primaryComparison = ageLevelComparison;
                    } else {
                        primaryComparison =
                            sortOrder === "ascending"
                                ? teamComparison
                                : -teamComparison;
                    }
                    break;
                case "firstName":
                    if (firstNameComparison === 0) {
                        if (teamComparison === 0) {
                            primaryComparison = ageLevelComparison;
                        } else {
                            primaryComparison = teamComparison;
                        }
                    } else {
                        primaryComparison =
                            sortOrder === "ascending"
                                ? firstNameComparison
                                : -firstNameComparison;
                    }
                    break;
                case "lastName":
                    if (lastNameComparison === 0) {
                        if (teamComparison === 0) {
                            primaryComparison = ageLevelComparison;
                        } else {
                            primaryComparison = teamComparison;
                        }
                    } else {
                        primaryComparison =
                            sortOrder === "ascending"
                                ? lastNameComparison
                                : -lastNameComparison;
                    }
                    break;
            }

            return primaryComparison;
        });
    }, [teams, sortColumn, sortOrder]);

    const contextValue = useMemo(
        () => ({
            fetchStatus,
            setFetchStatus,
            sortColumn,
            setSortColumn,
            sortOrder,
            setSortOrder,
            teams: sortedTeams,
            filters,
            setFilters,
        }),
        [sortedTeams],
    );

    return (
        <IndexContext.Provider value={contextValue}>
            {children}
        </IndexContext.Provider>
    );
};
